import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';

/**
 * Play — the three admin tools for the PLAY feature.
 *
 * Events, ambassador applications and the pairings board are all rebuilt and
 * route inside the tree — nothing here leaves it any more. The export does
 * its work here: it is a GET with an Authorization header, which is why it goes
 * through
 * `_playReport.downloadCsv` and a Blob rather than being a plain link.
 *
 * The export is not put behind a dialog. It reads and downloads; there is nothing
 * to undo and nothing to warn about.
 *
 * **`/play/players` is not rebuilt as its own page, deliberately.** That page in
 * the old admin is a paragraph and this same button — one route whose whole
 * content is a download that already sits here. What it did carry, and this card
 * did not, is a list of *what is in the file*: not "affiliations" but
 * affiliations grouped four ways, not "sports" but sports with level and
 * description. That is the difference between a label and documentation, and for
 * an export somebody hands to a colleague it is the part that decides whether it
 * answers their question. So the list came across and the route did not.
 *
 * The old route still works in the old admin, so nobody following a bookmark is
 * stranded.
 */

/**
 * What a row of the export holds, as the old page listed it.
 *
 * Worth keeping in this shape rather than as prose: somebody reads it looking for
 * one field, and a sentence makes them read all of it to find out.
 */
const EXPORT_CONTENTS = [
  ['User', 'id, name, email, phone, active status'],
  ['Profile', 'birth year, gender, expat / tourist / partner description'],
  ['Sports', 'each with level and description, plus availability slots'],
  ['Affiliations', 'grouped by education, workplace, club and neighbourhood'],
  ['Ambassador roles', 'affiliation, sport and status'],
  ['Dismissed banners', 'the key, and how many times it was dismissed'],
  ['Counts', 'invites sent, venues, and the profile’s timestamps']
];

const PlayInternal = ({ api }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const download = async () => {
    setBusy(true);
    setNote(null);
    try {
      await api.playReport.downloadCsv();
      setNote({ tone: 'info', text: 'CSV downloaded.' });
    } catch (error) {
      // The message is the server's when there is one — "failed" alone leaves you
      // guessing between an expired session and an export that timed out.
      setNote({ tone: 'error', text: `Download failed: ${error?.message || 'unknown error'}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header">
        <h1>Play</h1>
        <p className="next-muted">
          Admin tools for the PLAY feature: curated events, ambassador applications, and the full
          players export.
        </p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-cards">
        <Link className="next-cards__card" to="/next/play-events">
          <h2>
            Events
            <span className="next-badge next-badge--new">New</span>
          </h2>
          <p className="next-muted">
            Curated PLAY events (KSI Play Lunch and the like). Signups and watchlist per session,
            and the pairings board.
          </p>
          <span className="next-cards__go" aria-hidden>→</span>
        </Link>

        <Link className="next-cards__card" to="/next/ambassador-applications">
          <h2>Ambassador applications</h2>
          <p className="next-muted">
            Review pending applications, approve or decline. Filter by status for the history.
          </p>
          <span className="next-cards__go" aria-hidden>→</span>
        </Link>
      </div>

      <section className="next-card">
        <div className="next-card__head"><h2>Players export</h2></div>
        <div className="next-cards__body">
          <p className="next-muted">
            One row per PLAY profile in the database. Each row holds:
          </p>

          <dl className="next-deflist">
            {EXPORT_CONTENTS.map(([what, detail]) => (
              <React.Fragment key={what}>
                <dt>{what}</dt>
                <dd>{detail}</dd>
              </React.Fragment>
            ))}
          </dl>
          <button
            type="button"
            className="next-btn next-btn--primary next-btn--block"
            onClick={download}
            disabled={busy}
          >
            {busy ? 'Downloading…' : 'Download CSV'}
          </button>
        </div>
      </section>
    </div>
  );
};

PlayInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Play = withApi(PlayInternal);

Play.requiresLogin = true;

export default Play;
